import React, { useState, Fragment, useEffect } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import MoreHoriz from '@material-ui/icons/MoreHoriz';
import cogoToast from 'cogo-toast';
import OutsideClickHandler from 'react-outside-click-handler';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import CallPreviewEditPanel from './CallPreviewEditPanel';

import { regenerateSummary, reprocessCall } from '../../api/calls';

import { formatEmotion, getTimeComponentsFromNumber, getPrettyTimeDisplayFromNumber } from '../../helpers/formatting';
import { getEmotionColor, getEmotionalDuration, getEmotionalBreakdown } from '../../helpers/emotion_helpers';

import './CallPreview.scss';

import ParticipantBadge from '../Participants/ParticipantBadge';

const CallPreview = (props) => {
  const { call, searchVal, deleteCall, expandAllCards, selectForMatchingToDeal } = props;
  const { chat_name, duration_seconds, status, participants, summary, created_at, deal } = call;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showingMoreOptions, setShowingMoreOptions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEmotionSpreadMode, setEmotionSpreadMode] = useState(false);
  const [numVisibleActionItems, setNumVisibleActionItems] = useState(5);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  const NUM_ACTION_ITEMS_INITIALLY_VISIBLE = 5;
  const showMoreActionItems = () => setNumVisibleActionItems(numVisibleActionItems + 5);
  const showLessActionItems = () => setNumVisibleActionItems(5);

  const EMOTIONAL_DISPLAY_THRESHOLD = 16;
  const { minutes, seconds } = getTimeComponentsFromNumber(duration_seconds);
  const emotionBarDuration = getEmotionalDuration(call, selectedParticipant);
  const emotionalBreakdown = getEmotionalBreakdown(call, { participant: selectedParticipant });

  const formatWithSearch = (display) => {
    if (searchVal.length < 3) return display;
    let [indexes, i] = [[], -1];
    while ((i = display.toLowerCase().indexOf(searchVal, i + 1)) !== -1) indexes.push(i);
    if (!indexes.length) return display;
    return indexes.map((index, idx) => (
      <Fragment key={index}>
        <span>{display.slice(indexes[idx - 1] || 0, index)}</span>
        <span className='highlighted'>{display.slice(index, index + searchVal.length)}</span>
        {idx === indexes.length - 1 && <span>{display.slice(index + searchVal.length)}</span>}
      </Fragment>
    ));
  };

  const rerunAnalysis = () =>
    isFailed
      ? reprocessCall(call).then((resp) => {
          cogoToast.success(`Summary reproccessing has begun.`);
          props.syncCalls();
          setShowingMoreOptions(false);
        })
      : regenerateSummary(call).then((resp) => {
          cogoToast.success(`Summary regeneration has begun.`);
          props.syncCalls();
          setShowingMoreOptions(false);
        });

  const skipPromptToDeleteCall = (call) => promptToDeleteCall(call, true);
  const promptToDeleteCall = (call, skipPrompt = false) =>
    skipPrompt
      ? deleteCall(call)
      : confirmAlert({
          title: 'Just confirming',
          message: 'Are you sure you want to delete this call?',
          buttons: [
            { label: 'No', className: 'cancel', onClick: () => {} },
            {
              label: 'Yes',
              onClick: () => deleteCall(call),
            },
          ],
        });

  useEffect(() => {
    setIsExpanded(expandAllCards);
  }, [expandAllCards]);

  const sortedParticipants = _.orderBy(
    participants,
    [(p) => !p.is_host, (p) => !p.name.toLowerCase().includes(searchVal.toLowerCase()), (p) => parseFloat(p.speaking_duration_seconds || 0)],
    ['asc', 'asc', 'desc']
  );
  const hasHiddenEmotions = _.find(emotionalBreakdown, ({ percentage }) => percentage <= EMOTIONAL_DISPLAY_THRESHOLD);

  const isParticipantSelected = (p) => p?.id === selectedParticipant?.id;
  const toggleSelectedParticipant = (p) => setSelectedParticipant(isParticipantSelected(p) ? null : p);

  const isFailed = status === 'failed';
  const isProcessing = status === 'processing';
  const isComplete = status === 'complete';

  const health = call?.summary?.call_health_score;

  return (
    <div className={cn('call-outer-container', status)}>
      <div className='header'>
        <div className='main'>
          <div onClick={isProcessing ? () => {} : props.previewCall} to={`/calls/${call.chat_id || call.chat}`}>
            <h2 className='name'>{formatWithSearch(chat_name)}</h2>
          </div>
          {/* <Link to={`/calls/${call.chat_id || call.chat}`}> */}
          {/*   <h2 className='name'>{formatWithSearch(chat_name)}</h2> */}
          {/* </Link> */}
          <div className='meta'>
            {moment(created_at).format('MMMM Do')} at {moment(created_at).format('h:mm a')} • {minutes ? `${minutes} min` : ''} {seconds} sec
            {health && <span> • Health: {health}</span>}
            {!deal && !isFailed && (
              <span className='match-to-deal' onClick={selectForMatchingToDeal}>
                {` `}• No deal matched
                <span className='inline-btn'>MATCH TO DEAL</span>
              </span>
            )}
            {deal && <span onClick={selectForMatchingToDeal}>{deal ? ` • ${deal.name}` : ''}</span>}
          </div>
          {isFailed ? (
            <div className='status-message failed'>
              Summary failed, click{' '}
              <span onClick={rerunAnalysis} className='btn-link'>
                here
              </span>{' '}
              to rerun.
            </div>
          ) : (
            isProcessing && <div className='status-message'>Currently Processing Call..</div>
          )}
        </div>
        <div className='participants'>
          {sortedParticipants.slice(0, 3).map((p, idx) => (
            <ParticipantBadge
              key={p.name + idx}
              specialNameFormatting={formatWithSearch}
              clickParticipant={toggleSelectedParticipant}
              isSelected={isParticipantSelected(p)}
              isOtherSelected={!isParticipantSelected(p) && !!selectedParticipant}
              participant={p}
            />
          ))}
          {sortedParticipants.length > 3 && (
            <div
              onClick={() => toggleSelectedParticipant()}
              className={cn('participant-badge-outer-container', { 'not-selected': selectedParticipant })}
            >
              <div className='image extras'>+{sortedParticipants.length - 3}</div>
              <div className='name'>More</div>
            </div>
          )}
        </div>
      </div>
      {isComplete && (
        <>
          <div
            className='emotions'
            onMouseEnter={() => hasHiddenEmotions && setEmotionSpreadMode(true)}
            onMouseLeave={() => setEmotionSpreadMode(false)}
          >
            <div className='meter-container'>
              {_.map(emotionalBreakdown, ({ emotion, percentage, ratioFull }, idx) => {
                const percentageWidth = isEmotionSpreadMode ? 100 / emotionalBreakdown.length : percentage;
                const isVisible = percentage > EMOTIONAL_DISPLAY_THRESHOLD || isEmotionSpreadMode;
                return (
                  <div
                    key={emotion}
                    style={{
                      backgroundColor: getEmotionColor(emotion),
                      width: `${percentageWidth}%`,
                    }}
                    className='meter-segment'
                  >
                    {!isVisible && _.get(emotionalBreakdown, [idx - 1, 'percentage']) > EMOTIONAL_DISPLAY_THRESHOLD ? (
                      <div className='emotion-label'>
                        <span className='percentage more'>
                          +{emotionalBreakdown.filter((e) => e.percentage < EMOTIONAL_DISPLAY_THRESHOLD).length} more
                        </span>
                      </div>
                    ) : null}
                    <div
                      className={cn('emotion-label', {
                        expanded: isEmotionSpreadMode,
                        'expanded-and-shrunk': isEmotionSpreadMode && emotionalBreakdown.length > 5,
                        'expanded-and-extra-shrunk': isEmotionSpreadMode && emotionalBreakdown.length > 8,
                        hidden: !isVisible,
                      })}
                    >
                      <div className='emotion'>{formatEmotion(emotion).titleCase}</div>
                      <div className='percentage'>{getPrettyTimeDisplayFromNumber(emotionBarDuration * ratioFull)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className='body'>
            <div className='details-section'>
              <div className='details-section-header'>Call Summary</div>
              <div className='details-section-body'>{summary?.summary}</div>
            </div>
            {isExpanded && (
              <>
                {!!summary?.action_items?.length && (
                  <div className='details-section'>
                    <div className='details-section-header'>Action Items</div>
                    <div className='details-section-body'>
                      <ul className='action-items'>
                        {summary.action_items.slice(0, numVisibleActionItems).map((item, idx) => (
                          <li key={idx} className='action-item'>
                            {item}
                          </li>
                        ))}
                        {summary.action_items.length > NUM_ACTION_ITEMS_INITIALLY_VISIBLE && (
                          <>
                            {numVisibleActionItems >= summary.action_items.length ? (
                              <div className='show-more-less-toggle' onClick={showLessActionItems}>
                                Show Less
                              </div>
                            ) : (
                              <div className='show-more-less-toggle' onClick={showMoreActionItems}>
                                Show More
                              </div>
                            )}
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
      <div className={cn('show-more-options-icon', { 'options-visible': showingMoreOptions })}>
        <MoreHoriz onClick={() => setShowingMoreOptions(true)} />
        {showingMoreOptions && (
          <OutsideClickHandler onOutsideClick={() => setShowingMoreOptions(false)}>
            <div className='more-options-menu'>
              <div onClick={() => (isProcessing ? skipPromptToDeleteCall(call) : promptToDeleteCall(call))} className='options-action'>
                Delete Call
              </div>
              {window.__ALLOW_UPLOAD__ && (
                <>
                  <div onClick={() => setIsEditing(true)} className='options-action'>
                    Edit Participants
                  </div>
                  {!isProcessing && (
                    <div onClick={rerunAnalysis} className='options-action'>
                      {isFailed ? 'Retry' : 'Rerun'} Analysis
                    </div>
                  )}
                </>
              )}
            </div>
          </OutsideClickHandler>
        )}
      </div>
      {isEditing && (
        <CallPreviewEditPanel
          call={call}
          closePanel={() => setIsEditing(false)}
          closePanelAndResync={() => {
            setIsEditing(false);
            rerunAnalysis();
          }}
          updateChatParticipant={props.updateChatParticipant}
        />
      )}
    </div>
  );
};

CallPreview.propTypes = {
  call: PropTypes.object.isRequired,
  syncCalls: PropTypes.func.isRequired,
  expandAllCards: PropTypes.bool.isRequired,
  selectForMatchingToDeal: PropTypes.func.isRequired,
  updateChatParticipant: PropTypes.func.isRequired,
};

export default CallPreview;
